import { ApolloError } from '@apollo/client';
import { Col, TitleText } from '@policygenius/mortar';
import get from 'lodash/get';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import NavigatorContent from 'components/NavigatorContent';
import BasicInfoForm from 'containers/Basics/Info/form';
import clearCachedQuotedPolicies from 'graphql/util/clearCachedQuotedPolicies';
import consolidateKnownErrors from 'graphql/util/consolidateKnownErrors';
import { flattenErrorMessages } from 'util/createFormSubmissionError';

import { useUpdateBasicInfoMutation } from '../graphql/mutations/UpdateBasicInfo';
import { HEADER } from './constants';
import { BasicInfoFormValues } from './form/types';
import { basicInfoMutationError, basicInfoMutationComplete } from './hooks/commands';
import styles from './index.module.scss';
import { InfoProps } from './types';
import buildInitialValues from './util/buildInitialValues';

const Info: React.FC<InfoProps> = ({ activeLifeApplicationId, activeUserUuid, customer }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [updateBasicInfo] = useUpdateBasicInfoMutation({
    onError: (err) => basicInfoMutationError(err, dispatch),
    update: (cache) => clearCachedQuotedPolicies(cache, activeLifeApplicationId),
  });

  const initialValues = buildInitialValues(customer);

  const onSubmit = async (formInputs: BasicInfoFormValues) => {
    try {
      await updateBasicInfo({
        variables: {
          dateOfBirth: formInputs.dateOfBirth,
          gender: formInputs.gender,
          zipCode: formInputs.zipCode,
          uuid: activeUserUuid,
        },
      });

      basicInfoMutationComplete({
        activeUserUuid,
        dispatch,
        navigate,
      });
    } catch (error) {
      const definedErrors =
        error instanceof ApolloError &&
        (get(error, 'networkError.result.errors') || consolidateKnownErrors(error));

      if (definedErrors) {
        return flattenErrorMessages(definedErrors);
      }
    }
  };

  return (
    <NavigatorContent>
      <Col
        className={styles.header}
        push={{ medium: 2, large: 3 }}
        span={{ small: 4, medium: 8, large: 6 }}
      >
        <TitleText as="h1" size="large" className={styles.headline}>
          {HEADER}
        </TitleText>
      </Col>
      <BasicInfoForm
        activeUserUuid={activeUserUuid}
        initialValues={initialValues}
        onSubmit={onSubmit}
      />
    </NavigatorContent>
  );
};

export default Info;
